import type { jsPDF } from 'jspdf';
import { Token, Tokens, lexer } from 'marked';

/**
 * Markdown → PDF (A4) com `jspdf`; o `marked` só quebra o markdown em blocos, sem HTML nem
 * html2canvas, para o texto do PDF continuar selecionável. Formatação inline vira texto simples.
 */
const MARGIN = 48;
const LINE = 1.45;
const HEADING_SIZES = [18, 15, 13, 12, 11, 11];

/** Texto puro de um trecho inline de markdown. */
export function inlinePlain(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '[imagem: $1]')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

class Writer {
  private y = MARGIN;
  private readonly width: number;
  private readonly bottom: number;

  constructor(private readonly doc: jsPDF) {
    this.width = doc.internal.pageSize.getWidth() - MARGIN * 2;
    this.bottom = doc.internal.pageSize.getHeight() - MARGIN;
  }

  private ensure(height: number): void {
    if (this.y + height > this.bottom) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  gap(points: number): void {
    this.y += points;
  }

  /** Texto quebrado na largura, a partir de `indent`. */
  text(
    content: string,
    opts: { size?: number; bold?: boolean; mono?: boolean; indent?: number; color?: number } = {}
  ): void {
    const size = opts.size ?? 10;
    const indent = opts.indent ?? 0;
    this.doc
      .setFont(opts.mono ? 'courier' : 'helvetica', opts.bold ? 'bold' : 'normal')
      .setFontSize(size)
      .setTextColor(opts.color ?? 30);
    const lines = this.doc.splitTextToSize(content, this.width - indent) as string[];
    for (const line of lines) {
      this.ensure(size * LINE);
      this.doc.text(line, MARGIN + indent, this.y + size);
      this.y += size * LINE;
    }
  }

  title(text: string): void {
    this.text(text, { size: 16, bold: true });
    this.rule(4);
  }

  heading(token: Tokens.Heading): void {
    const size = HEADING_SIZES[token.depth - 1] ?? 11;
    this.gap(token.depth <= 2 ? 10 : 6);
    this.ensure(size * 3);
    this.text(inlinePlain(token.text), { size, bold: true, color: 20 });
    if (token.depth <= 2) this.rule(2);
    this.gap(2);
  }

  rule(after = 6): void {
    this.ensure(8);
    this.doc.setDrawColor(210).setLineWidth(0.6).line(MARGIN, this.y + 2, MARGIN + this.width, this.y + 2);
    this.y += 2 + after;
  }

  list(token: Tokens.List, indent = 0): void {
    const start = typeof token.start === 'number' ? token.start : 1;
    token.items.forEach((item, index) => {
      const marker = token.ordered ? `${start + index}.` : item.task ? (item.checked ? '[x]' : '[ ]') : '•';
      const nested = item.tokens.filter(t => t.type === 'list') as Tokens.List[];
      const text = item.tokens
        .filter(t => t.type !== 'list')
        .map(t => ('text' in t ? (t as { text: string }).text : ''))
        .join(' ');

      this.doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(30);
      this.ensure(15);
      this.doc.text(marker, MARGIN + indent, this.y + 10);
      this.text(inlinePlain(text), { indent: indent + 16 });
      this.gap(2);
      nested.forEach(sub => this.list(sub, indent + 16));
    });
    this.gap(4);
  }

  code(text: string): void {
    this.doc.setFont('courier', 'normal').setFontSize(8.5);
    const lines = this.doc.splitTextToSize(text, this.width - 16) as string[];
    const lineHeight = 8.5 * LINE;
    let index = 0;
    while (index < lines.length) {
      this.ensure(lineHeight + 12);
      const fit = Math.max(1, Math.floor((this.bottom - this.y - 12) / lineHeight));
      const chunk = lines.slice(index, index + fit);
      const height = chunk.length * lineHeight + 12;
      this.doc.setFillColor(244, 246, 248).rect(MARGIN, this.y, this.width, height, 'F');
      this.doc.setFont('courier', 'normal').setFontSize(8.5).setTextColor(40);
      chunk.forEach((line, i) => this.doc.text(line, MARGIN + 8, this.y + 6 + 8.5 + i * lineHeight));
      this.y += height;
      index += chunk.length;
    }
    this.gap(8);
  }

  blockquote(text: string): void {
    const before = this.y;
    this.text(inlinePlain(text), { indent: 14, color: 90 });
    this.doc.setDrawColor(200).setLineWidth(2).line(MARGIN + 4, before + 2, MARGIN + 4, this.y - 2);
    this.gap(6);
  }

  table(token: Tokens.Table): void {
    const columns = token.header.length;
    const colWidth = this.width / columns;
    const size = 8.5;
    const row = (cells: string[], header: boolean) => {
      this.doc.setFont('helvetica', header ? 'bold' : 'normal').setFontSize(size);
      const wrapped = cells.map(cell => this.doc.splitTextToSize(cell, colWidth - 8) as string[]);
      const height = Math.max(...wrapped.map(lines => lines.length)) * size * LINE + 8;
      this.ensure(height);
      if (header) this.doc.setFillColor(238, 241, 245).rect(MARGIN, this.y, this.width, height, 'F');
      this.doc.setDrawColor(205).setLineWidth(0.5);
      wrapped.forEach((lines, col) => {
        const x = MARGIN + col * colWidth;
        this.doc.rect(x, this.y, colWidth, height);
        this.doc.setTextColor(30);
        lines.forEach((line, i) => this.doc.text(line, x + 4, this.y + 4 + size + i * size * LINE));
      });
      this.y += height;
    };
    row(token.header.map(cell => inlinePlain(cell.text)), true);
    token.rows.forEach(cells => row(cells.map(cell => inlinePlain(cell.text)), false));
    this.gap(10);
  }

  block(token: Token): void {
    switch (token.type) {
      case 'heading':
        return this.heading(token as Tokens.Heading);
      case 'paragraph':
        this.text(inlinePlain((token as Tokens.Paragraph).text));
        return this.gap(6);
      case 'list':
        return this.list(token as Tokens.List);
      case 'code':
        return this.code((token as Tokens.Code).text);
      case 'table':
        return this.table(token as Tokens.Table);
      case 'blockquote':
        return this.blockquote((token as Tokens.Blockquote).text);
      case 'hr':
        return this.rule(8);
      case 'space':
        return;
      default:
        if ('text' in token && token.text) {
          this.text(inlinePlain(String(token.text)));
          this.gap(6);
        }
    }
  }
}

/** Desenha `markdown` (com `title` no topo, se houver) num PDF A4 novo e devolve o jsPDF. */
export async function markdownToPdf(markdown: string, title?: string): Promise<jsPDF> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const writer = new Writer(doc);

  if (title) writer.title(title);
  for (const token of lexer(markdown ?? '')) writer.block(token);

  return doc;
}
